<template>
  <div class="section-card">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <span class="title">📺 主播列表</span>
        <span class="count-badge">{{ totalCount }}位主播</span>
      </div>
      <div class="toolbar-right">
        <NInput :value="searchKeyword" @update:value="emit('update:searchKeyword', $event)"
          @keyup.enter="emit('search')" placeholder="搜索主播..." size="small" clearable class="search-input" />
        <NButton size="small" type="primary" @click="emit('search')" class="search-btn">搜索</NButton>
        <div class="sort-group">
          <NButton size="tiny" :type="sortBy === 'credit' ? 'primary' : 'default'" @click="emit('update:sortBy', 'credit')" ghost>💰 收益</NButton>
          <NButton size="tiny" :type="sortBy === 'id' ? 'primary' : 'default'" @click="emit('update:sortBy', 'id')" ghost>📅 入驻</NButton>
        </div>
      </div>
    </div>

    <!-- 主播卡片列表 -->
    <div class="user-list">
      <div class="user-card" v-for="v in userList" :key="v.userName" :class="cardTint(v.totalWinAmount)">
        <!-- 名字行 -->
        <div class="card-row row-top">
          <span class="live-dot" :class="v.betCount > 0 ? 'online' : 'offline'"></span>
          <span class="user-name" @click.stop="goDetail(v.userName)">{{ v.userName }}</span>
          <span class="user-nickname" v-if="v.nickname">{{ v.nickname }}</span>
          <span class="status-tag" :class="v.betCount > 0 ? 'live' : 'rest'">
            {{ v.betCount > 0 ? '直播中' : '休息中' }}
          </span>
        </div>

        <!-- 统计行 -->
        <div class="card-row row-stats" v-if="debugMode">
          <span class="stat-val" :class="netWinColor(v.totalWinAmount)">
            {{ Tools.formatMoney(v.totalWinAmount, { showSign: true }) }}
          </span>
          <span class="stat-val dim">{{ (v.betCount ?? 0).toLocaleString() }} 场</span>
        </div>

        <!-- 底部行：金额 + 操作 -->
        <div class="card-row row-bottom">
          <span class="money" :class="{ pulse: refreshingUser === v.userName }">
            <i>💰</i>{{ Tools.formatMoney(v.credit) }}
          </span>
          <span class="card-refresh" :class="{ spinning: refreshingUser === v.userName }" @click.stop="emit('refreshUser', v)" title="刷新收益">⟳</span>
          <span class="spacer"></span>
          <span v-if="debugMode" class="protect-badge" :class="{ active: v.noClear }" @click.stop="emit('toggleNoClear', v)" title="保护：勾选后不受数据清理影响">
            {{ v.noClear ? '🔒' : '🔓' }}
          </span>
          <NButton size="small" type="primary" ghost @click.stop="emit('modifyBalance', v)">调整</NButton>
        </div>
      </div>

      <div v-if="userList.length === 0" class="empty-tip">
        <span class="empty-icon">📭</span>
        <span>暂无主播数据</span>
      </div>
    </div>

    <!-- 底部分页 -->
    <div class="pagination-bar">
      <NPagination :page="currentPage" @update:page="emit('update:currentPage', $event); emit('pageChange', $event)"
        :page-count="totalPages" :page-slot="7" size="small" />
    </div>

    <!-- 全局刷新悬浮按钮（漂浮在面板右下角）-->
    <span class="global-refresh" :class="{ spinning: refreshingAll }" @click="emit('refreshAll')" title="刷新全部主播收益">⟳</span>
  </div>
</template>

<script setup lang="ts">
import { NButton, NInput, NPagination } from 'naive-ui';
import { useRouter } from 'vue-router';
import { inject, ref, type Ref } from 'vue';
import Tools from '@/ts/Tools';

const router = useRouter();
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));

defineProps<{
  userList: any[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  searchKeyword: string;
  sortBy: string;
  refreshingUser: string | null;
  refreshingAll: boolean;
}>();

const emit = defineEmits<{
  'update:searchKeyword': [value: string];
  'update:currentPage': [value: number];
  'update:sortBy': [value: string];
  search: [];
  pageChange: [page: number];
  refreshUser: [user: any];
  refreshAll: [];
  modifyBalance: [user: any];
  toggleNoClear: [user: any];
}>();

function goDetail(userName: string) {
  router.push({ name: 'accountDetail', params: { userName } });
}

function netWinColor(val: number | undefined | null): string {
  if (val == null || val === 0) return 'zero';
  return val > 0 ? 'positive' : 'negative';
}

function cardTint(val: number | undefined | null): string {
  if (val == null || val === 0) return '';
  return val > 0 ? 'card-positive' : 'card-negative';
}
</script>

<style scoped lang="scss">
.section-card {
  position: relative;
  background-color: rgb(51, 51, 51);
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

// ========== 工具栏 ==========
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .title {
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
    letter-spacing: 0.5px;
  }

  .count-badge {
    font-size: 11px;
    color: #a78bfa;
    background: rgba(167, 139, 250, 0.12);
    padding: 2px 10px;
    border-radius: 10px;
    font-weight: 600;
  }

  .search-input {
    width: 140px;
    flex-shrink: 0;
  }

  .search-btn {
    height: 28px;
  }

  .sort-group {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: 4px;
  }
}

// ========== 底部分页 ==========
.pagination-bar {
  display: flex;
  justify-content: center;
  padding-top: 4px;
  flex-shrink: 0;
}

// ========== 卡片列表 ==========
.user-list {
  flex: 1;
  height: 100%;
  min-height: 0;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  gap: 12px;
  align-content: start;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
}

// ========== 主播卡片 ==========
.user-card {
  background: linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.1);
  }

  &.card-positive { border-top: 2px solid rgba(74, 222, 128, 0.4); }
  &.card-negative { border-top: 2px solid rgba(248, 113, 113, 0.4); }

  // 通用行
  .card-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
  }

  .row-top {
    padding: 6px 8px 2px;
  }

  .row-stats {
    padding: 2px 8px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }

  .row-bottom {
    padding: 4px 8px 6px;
  }

  // 在线圆点
  .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;

    &.online {
      background: #4ade80;
      box-shadow: 0 0 5px rgba(74, 222, 128, 0.5);
      animation: livePulse 2s ease-in-out infinite;
    }
    &.offline { background: #666; }
  }

  // 名字
  .user-name {
    font-size: 13px;
    font-weight: 700;
    color: #f1f5f9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 110px;
    cursor: pointer;
    &:hover { color: #c4b5fd; }
  }

  .user-nickname {
    font-size: 10px;
    color: #888;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 64px;
  }

  .status-tag {
    margin-left: auto;
    font-size: 9px;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 6px;
    white-space: nowrap;
    flex-shrink: 0;

    &.live { color: #4ade80; background: rgba(74, 222, 128, 0.12); }
    &.rest { color: #555; }
  }

  // 统计行
  .stat-val {
    font-size: 11px;
    font-weight: 700;
    font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;

    &.positive { color: #4ade80; }
    &.negative { color: #f87171; }
    &.zero     { color: #777; }
    &.dim      { color: #94a3b8; }
  }

  // 金额
  .money {
    font-size: 14px;
    font-weight: 700;
    color: #fbbf24;
    font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    letter-spacing: 0.3px;
    white-space: nowrap;

    i {
      font-style: normal;
      margin-right: 3px;
    }

    &.pulse { animation: pulse 0.8s ease-in-out infinite; }
  }

  .card-refresh {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    line-height: 1;
    flex-shrink: 0;

    &:hover { color: #a78bfa; }
    &.spinning {
      animation: spin 0.8s linear infinite;
      color: #a78bfa;
    }
  }

  .spacer { flex: 1; }

  .protect-badge {
    font-size: 13px;
    cursor: pointer;
    opacity: 0.4;
    line-height: 1;
    flex-shrink: 0;

    &:hover { opacity: 0.8; }
    &.active { opacity: 0.9; }
  }
}

// ========== 空状态 ==========
.empty-tip {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 60px 40px;
  color: #555;
  font-size: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.01);

  .empty-icon { font-size: 36px; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

// ========== 全局刷新悬浮按钮（漂浮在面板右下角）==========
.global-refresh {
  position: absolute;
  bottom: 5px;
  right: 24px;
  z-index: 10;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #ccc;
  background: rgba(99, 102, 241, 0.25);
  border: 1px solid rgba(167, 139, 250, 0.3);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.25s;
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

  &:hover {
    color: #fff;
    background: rgba(99, 102, 241, 0.45);
    border-color: rgba(167, 139, 250, 0.6);
    box-shadow: 0 4px 24px rgba(99, 102, 241, 0.3);
    transform: scale(1.08);
  }

  &.spinning {
    animation: spin 0.8s linear infinite;
    color: #fff;
    background: rgba(167, 139, 250, 0.4);
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@keyframes livePulse {
  0%, 100% { box-shadow: 0 0 4px rgba(74, 222, 128, 0.4); }
  50% { box-shadow: 0 0 12px rgba(74, 222, 128, 0.8); }
}
</style>
